import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, Theme } from '@mui/material/styles';
import { CadPollState } from '../../hooks/useCadRecord';
import {
  CadVerificationItem,
  CadVerificationResult,
  CadVerificationResultName,
  CadVerificationTiming,
  CallAttachment,
} from '../../types/Call';
import { SectionLabel, StageWaitBanner, StageWaitWording, StatusBanner } from './CadRecordParts';
import { formatDateTime, tally, timingRows, visuallyHidden } from './cadRecordDisplay';

/**
 * Chip colors per result. Every chip carries its result as text, so color is never the only
 * signal. Mismatch uses the dark warning shade: white on warning.main is below 4.5:1 at chip size.
 */
const RESULT_STYLE: Record<
  CadVerificationResultName,
  { chip: (theme: Theme) => string; tint: (theme: Theme) => string | undefined }
> = {
  Match: { chip: (t) => t.palette.success.main, tint: () => undefined },
  Mismatch: { chip: (t) => t.palette.warning.dark, tint: (t) => t.palette.warning.main },
  Missing: { chip: (t) => t.palette.error.main, tint: (t) => t.palette.error.main },
  'Not on call': { chip: (t) => t.palette.info.main, tint: (t) => t.palette.info.main },
  Unclear: { chip: (t) => t.palette.grey[700], tint: () => undefined },
};

/** The section heading; the tab moves focus here when the button that started a check unmounts. */
export const VERIFICATION_HEADING_ID = 'cad-verification-heading';

const VERIFICATION_WAIT: StageWaitWording = {
  queued: 'Queued to check the CAD entry against the call',
  running: 'Checking the CAD entry against the call',
  body: 'The result appears here automatically, usually within a minute.',
  stillTitle: 'Still checking',
  stillBody: 'The check has taken more than 5 minutes. It is still queued on the server.',
  unavailableBody: 'The check status could not be read. Refresh the page or sign in again.',
};

const describeVerification = (
  record: CallAttachment,
  pollState: CadPollState,
  result: CadVerificationResult | undefined,
  loadError: boolean
): string => {
  switch (record.verificationStatus) {
    case 'NotRun':
      return '';
    case 'Pending':
    case 'Verifying':
      if (pollState === 'timedOut') return 'Still checking the CAD entry after 5 minutes.';
      if (pollState === 'unavailable') return 'The check status could not be read.';
      return record.verificationStatus === 'Pending'
        ? 'Queued to check the CAD entry against the call.'
        : 'Checking the CAD entry against the call.';
    case 'VerificationFailed':
      return 'The check against the call could not be completed.';
    case 'Verified':
      if (loadError) return 'The check result could not be loaded.';
      return result
        ? `Check complete: ${tally(result.items) || 'no items'}.`
        : 'Loading the check result.';
  }
};

interface CadVerificationListProps {
  record: CallAttachment;
  canEdit: boolean;
  pollState: CadPollState;
  result: CadVerificationResult | undefined;
  loadError: boolean;
  requesting: boolean;
  onLoad: () => void;
  onRequest: () => void;
  onResume: () => void;
}

/** The CAD entry checked against the transcript: status while it runs, then the findings and timing. */
export const CadVerificationList: React.FC<CadVerificationListProps> = ({
  record,
  canEdit,
  pollState,
  result,
  loadError,
  requesting,
  onLoad,
  onRequest,
  onResume,
}) => {
  const requestButton = (label: string) =>
    canEdit && (
      <Button
        variant="outlined"
        size="small"
        onClick={onRequest}
        disabled={requesting}
        startIcon={requesting ? <CircularProgress size={14} aria-hidden /> : undefined}
      >
        {label}
      </Button>
    );

  const renderBody = () => {
    switch (record.verificationStatus) {
      case 'NotRun':
        return (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" mb={canEdit ? 1.5 : 0}>
              {canEdit
                ? 'Compare the CAD entry with what the caller said on the recording. Each item is marked Match, Mismatch, Missing or Not on call.'
                : 'The CAD entry has not been checked against the call yet.'}
            </Typography>
            {requestButton('Check against the call')}
          </Paper>
        );

      case 'Pending':
      case 'Verifying':
        return (
          <StageWaitBanner
            queued={record.verificationStatus === 'Pending'}
            pollState={pollState}
            wording={VERIFICATION_WAIT}
            onResume={onResume}
          />
        );

      case 'VerificationFailed':
        return (
          <StatusBanner
            tone="error"
            title="The check could not be completed"
            action={requestButton('Run the check again')}
          >
            {record.verificationError || 'Verification failed.'}
          </StatusBanner>
        );

      case 'Verified':
        if (loadError) {
          return (
            <StatusBanner
              tone="error"
              title="The check result could not be loaded"
              action={
                <Button variant="outlined" size="small" onClick={onLoad}>
                  Load again
                </Button>
              }
            >
              The check finished, but its result did not load.
            </StatusBanner>
          );
        }
        if (!result) {
          return (
            <Box display="flex" alignItems="center" gap={1.5}>
              <CircularProgress size={16} aria-hidden />
              <Typography variant="body2" color="text.secondary">
                Loading the check result…
              </Typography>
            </Box>
          );
        }
        return <VerifiedResult record={record} result={result} />;
    }
  };

  return (
    <Stack spacing={1.5} component="section" aria-labelledby={VERIFICATION_HEADING_ID}>
      <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
        {describeVerification(record, pollState, result, loadError)}
      </Box>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="baseline"
        columnGap={2}
      >
        <SectionLabel id={VERIFICATION_HEADING_ID} focusable>
          CAD entry checked against the call
        </SectionLabel>
        {record.verificationStatus === 'Verified' && result && result.items.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {tally(result.items)}
          </Typography>
        )}
      </Box>
      {renderBody()}
    </Stack>
  );
};

/**
 * A finished check: summary, findings, timing and the model caption. `dense` is the assistant's
 * form of the same block for a narrow pane: a heading line with the tally, smaller text, one
 * column of timing.
 */
export const VerifiedResult: React.FC<{
  record: CallAttachment;
  result: CadVerificationResult;
  dense?: boolean;
}> = ({ record, result, dense }) => (
  <Stack spacing={dense ? 1.25 : 2}>
    {dense && (
      // Text, not a heading element: the pane renders markdown headings the same way.
      <Typography
        component="p"
        variant="subtitle2"
        sx={{ fontSize: '0.8rem', borderBottom: 1, borderColor: 'divider', pb: 0.5 }}
      >
        CAD entry against what was said
        {result.items.length > 0 && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {tally(result.items)}
          </Typography>
        )}
      </Typography>
    )}
    {result.summary && (
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere', ...(dense && { fontSize: '0.8rem' }) }}>
        {result.summary}
      </Typography>
    )}

    {result.items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        The check returned no items.
      </Typography>
    ) : (
      <Stack component="ul" spacing={dense ? 0.75 : 1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {result.items.map((item, index) => (
          <FindingRow key={`${index}-${item.item}`} item={item} />
        ))}
      </Stack>
    )}

    {result.timing && <TimingBlock timing={result.timing} dense={dense} />}

    {record.verifiedAt && (
      <Typography variant="caption" color="text.secondary">
        Checked {formatDateTime(record.verifiedAt)}
        {record.verificationModel ? ` with ${record.verificationModel}` : ''}. Findings are the
        model&apos;s reading of the recording and the report; confirm them before acting.
      </Typography>
    )}
  </Stack>
);

const FindingRow: React.FC<{ item: CadVerificationItem }> = ({ item }) => {
  const style = RESULT_STYLE[item.result] ?? RESULT_STYLE.Unclear;
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 1,
        bgcolor: (theme) => {
          const tint = style.tint(theme);
          return tint
            ? alpha(tint, theme.palette.mode === 'dark' ? 0.16 : 0.08)
            : theme.palette.action.hover;
        },
      }}
    >
      <Chip
        label={item.result}
        size="small"
        sx={{
          flexShrink: 0,
          fontWeight: 600,
          color: 'common.white',
          bgcolor: (theme) => style.chip(theme),
        }}
      />
      <Box minWidth={0} sx={{ overflowWrap: 'anywhere' }}>
        <Typography variant="body2">
          <strong>{item.item}.</strong>
          {item.notes ? ` ${item.notes}` : ''}
        </Typography>
        {(item.callerSaid || item.cadContains) && (
          <Box component="dl" sx={{ m: 0, mt: 0.5 }}>
            {item.callerSaid && <Evidence label="Caller said" value={item.callerSaid} />}
            {item.cadContains && <Evidence label="CAD has" value={item.cadContains} />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Evidence: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box display="flex" gap={0.75}>
    <Typography component="dt" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}:
    </Typography>
    <Typography component="dd" variant="caption" sx={{ m: 0 }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Timing as the check reported it: event log times copied as printed, and the intervals the model
 * computed. Nothing is derived in the browser; an interval the check did not compute is not shown.
 */
const TimingBlock: React.FC<{ timing: CadVerificationTiming; dense?: boolean }> = ({
  timing,
  dense,
}) => {
  const rows = timingRows(timing);

  if (rows.length === 0 && !timing.implication) return null;

  return (
    <Box>
      <Typography
        component={dense ? 'p' : 'h3'}
        variant="subtitle2"
        sx={
          dense
            ? { fontSize: '0.8rem', borderBottom: 1, borderColor: 'divider', pb: 0.5, mb: 0.75 }
            : { mb: 1 }
        }
      >
        Timing from the CAD event log
      </Typography>
      {rows.length > 0 && dense && (
        <Box
          component="dl"
          sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.25 }}
        >
          {rows.map(({ label, value }) => (
            <React.Fragment key={label}>
              <Typography component="dt" variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography component="dd" variant="caption" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}
      {rows.length > 0 && !dense && (
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            rowGap: 1.5,
            columnGap: 2,
          }}
        >
          {rows.map(({ label, value }) => (
            <Box key={label} minWidth={0}>
              <Typography component="dt" variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {timing.implication && (
        <Typography
          variant="body2"
          color="text.secondary"
          mt={dense ? 0.75 : 1.5}
          sx={{ overflowWrap: 'anywhere', ...(dense && { fontSize: '0.8rem' }) }}
        >
          {timing.implication}
        </Typography>
      )}
    </Box>
  );
};
